import { useMutation } from "@tanstack/react-query";
import { useAxios, useApiResponseParser } from "../";

export type MetadataRow = {
    Id: string;
    OrganizationId: string;
    Scope: string;
    ScopeId: string;
    MetaKey: string;
    MetaValue: any;
    CreatedBy?: string | null;
    UpdatedBy?: string | null;
    CreatedAt?: string;
    UpdatedAt?: string;
};

export type ListMetadataParams = {
    OrganizationId: string;
    Scope: string;
    ScopeId: string;
    MetaKey?: string;
};

export type CreateMetadataParams = {
    OrganizationId: string;
    Scope: string;
    ScopeId: string;
    MetaKey: string;
    MetaValue: any;
};

export type UpdateMetadataParams = {
    Id: string;
    MetaValue: any;
};

export default function useMetadata() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();

    function useList() {
        return useMutation({
            mutationFn: (params: ListMetadataParams) =>
                axiosPrivate
                    .get(`/v1/metadata/list`, { params })
                    .then(parseSuccessResponseData) as Promise<MetadataRow[]>,
        });
    }

    function useGetById() {
        return useMutation({
            mutationFn: (id: string) =>
                axiosPrivate
                    .get(`/v1/metadata/${id}`)
                    .then(parseSuccessResponseData) as Promise<MetadataRow>,
        });
    }

    function useCreate() {
        return useMutation({
            mutationFn: (parameters: CreateMetadataParams) =>
                axiosPrivate
                    .post(`/v1/metadata`, parameters)
                    .then(parseSuccessResponseData) as Promise<MetadataRow>,
        });
    }

    function useUpdate() {
        return useMutation({
            mutationFn: (parameters: UpdateMetadataParams) =>
                axiosPrivate
                    .put(`/v1/metadata/update`, parameters)
                    .then(parseSuccessResponseData) as Promise<MetadataRow>,
        });
    }

    function useDelete() {
        return useMutation({
            mutationFn: (id: string) =>
                axiosPrivate
                    .delete(`/v1/metadata/${id}`)
                    .then(parseSuccessResponseData),
        });
    }

    return {
        useList,
        useGetById,
        useCreate,
        useUpdate,
        useDelete,
    };
}
